import { ActionTable } from '@/components';
import { FormController, FormInput } from '@/components/form';
import { useAppSelector } from '@/state';
import { ProcurementApi } from '@/state/api';
import { ProcurementType } from '@/types';
import { getPurchaseOrderNumberError } from '@/utils/purchaseOrderDisplay';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormatNumber,
  Image,
  Separator,
  Text,
} from '@chakra-ui/react';
import { OverlayDialog, toasterApi } from '@ui';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import z from 'zod';

type PostDraftOrderType = {
  status: string;
  title: string;
  project: number;
  supplier: number;
  created_by: number;
  function_number?: string;
} | null;

type CreatePurchaseOrderType =
  | {
      // New draft does not have draft id and not required
      isNew: true;
      draftId?: never;
      release_numbers?: never;

      supplierName?: string;
      draftOrder?: PostDraftOrderType;
      draftOrderLines?: GetDraftOrderLineType[];
      onClickBack: () => void;
      onSubmit?: () => void;
    }
  | {
      // Existing draft has draft id and required
      isNew?: false | undefined;
      draftId: number;
      release_numbers: string[];

      supplierName?: string;
      draftOrder?: PostDraftOrderType;
      draftOrderLines?: GetDraftOrderLineType[];
      onClickBack: () => void;
      onSubmit?: () => void;
    };
const CreatePurchaseOrder = (prop: CreatePurchaseOrderType) => {
  const {
    supplierName,
    draftOrder,
    draftOrderLines,
    onClickBack,
    draftId,
    isNew,
    onSubmit,
    release_numbers,
  } = prop;

  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );
  const currentUser = useAppSelector((state) => state.auth.current.user);

  const calculatedPrice = useMemo(() => {
    const total =
      draftOrderLines?.reduce((acc, item) => {
        const qty = Number(item?.qty ?? 0);
        const unit = Number(item?.unit_price ?? 0);
        const lineTotal =
          Number.isFinite(qty) && Number.isFinite(unit) ? qty * unit : 0;
        return acc + lineTotal;
      }, 0) ?? 0;

    return { total };
  }, [draftOrderLines]);

  const [postDraftOrdersMutation] =
    ProcurementApi.Api.usePostDraftOrdersMutation();
  const [postDraftOrderLinesMutation] =
    ProcurementApi.Api.usePostDraftOrderLinesMutation();
  const [postPurchaseOrderMutation] =
    ProcurementApi.Api.usePostPurchaseOrderMutation();

  const handleConvertToPo = () => {
    OverlayDialog.open('convertToPo', {
      overlayId: 'convertToPo',
      title: 'Convert to PO',
      description: `Are you sure you want to convert this ${isNew ? 'new' : 'draft'} order to PO?`,
      content: (
        <FormController
          size="lg"
          formSchema={submitOrderToPoSchema}
          defaultValues={{
            expected_delivery_date: '',
            project_po_number: '',
          }}
          onFormSubmit={async (formData) => {
            if (!dayjs(formData.expected_delivery_date).isValid()) return;

            if (!isNew) {
              if (!draftId) return;
              try {
                const result = await postPurchaseOrderMutation({
                  body: {
                    expected_delivery_date: formData.expected_delivery_date,
                    project_po_number:
                      formData.project_po_number?.trim() || null,
                  },
                  params: {
                    draft_id: draftId,
                  },
                }).unwrap();

                toasterApi({
                  type: 'success',
                  status: 200,
                  code: 'Success',
                  message: `Purchase order ${result.project_po_number ?? result.po_number} created successfully`,
                });
              } catch (error) {
                toasterApi({
                  type: 'error',
                  status: 400,
                  code: 'Error',
                  message: getPurchaseOrderNumberError(
                    error,
                    'Could not create purchase order',
                  ),
                });
                return;
              }
            } else {
              try {
                if (!draftOrder || !draftOrderLines) return; // Both draftOrder and draftOrderLines are required

                const draftOrderResponse = await postDraftOrdersMutation({
                  body: draftOrder,
                }).unwrap();

                const payload: ProcurementType.DraftOrderLines.Post['body'] =
                  draftOrderLines?.map((line) => ({
                    qty: line.qty,
                    unit_price: line.unit_price,
                    needed_by: dayjs(line.needed_by).toISOString(),
                    notes: line.notes,
                    ...(line.part_id && line.part_id > 0
                      ? { part: line.part_id }
                      : { manual_part_number: line.part_number! }),
                    release: line?.release as number,
                  }));

                await postDraftOrderLinesMutation({
                  body: payload,
                  params: {
                    draft_id: draftOrderResponse?.id,
                  },
                }).unwrap();

                const result = await postPurchaseOrderMutation({
                  body: {
                    expected_delivery_date: formData.expected_delivery_date,
                    project_po_number:
                      formData.project_po_number?.trim() || null,
                  },
                  params: {
                    draft_id: draftOrderResponse?.id,
                  },
                }).unwrap();

                toasterApi({
                  type: 'success',
                  status: 200,
                  code: 'Success',
                  message: `Purchase order ${result.project_po_number ?? result.po_number} created successfully`,
                });
              } catch (error) {
                toasterApi({
                  type: 'error',
                  status: 400,
                  code: 'Error',
                  message: getPurchaseOrderNumberError(
                    error,
                    'Could not create purchase order',
                  ),
                });
                return;
              }
            }

            // On submit callback
            onSubmit?.();

            // Close overlay
            OverlayDialog.close('convertToPo');
          }}
        >
          {({
            register,
            control,
            errors,
          }: {
            register: UseFormRegister<SubmitOrderToPoType>;
            control: Control<SubmitOrderToPoType>;
            errors: FieldErrors<SubmitOrderToPoType>;
          }) => {
            return (
              <Flex gapX={4} flexWrap="wrap">
                <FormInput
                  maxW="300px"
                  register={register}
                  control={control}
                  errors={errors}
                  id="expected_delivery_date"
                  label="Expected Delivery Date"
                  showLabelAtTop
                  required
                  type="date"
                />
                <FormInput
                  maxW="340px"
                  register={register}
                  control={control}
                  errors={errors}
                  id="project_po_number"
                  label="Purchase Order Number Override (Optional)"
                  placeholder={
                    selectedProject?.item?.code
                      ? `${selectedProject.item.code}-025`
                      : '025'
                  }
                  helperText="Enter an unused numeric suffix or the full matching project number. Leave blank for the next automatic number."
                  showLabelAtTop
                />
              </Flex>
            );
          }}
        </FormController>
      ),
    });
  };

  const columns: ActionTable.ColumnsConfigType<
    GetDraftOrderLineType,
    {
      index: number;
      total: number;
    }
  > = [
    {
      index: {
        label: '#',
        width: '50px',
      },
    },
    {
      part_number: {
        label: 'Part #',
        width: '100px',
      },
    },
    ...(isNew
      ? [
          {
            release: {
              label: 'Release',
              width: '100px',
            },
          },
        ]
      : []),
    {
      needed_by: {
        label: 'Needed By',
        width: '150px',
      },
    },
    {
      qty: {
        label: 'Qty',
        width: '50px',
      },
    },
    {
      unit_price: {
        label: 'Unit Price',
        width: '100px',
      },
    },
    {
      total: {
        label: 'Total',
        width: '100px',
      },
    },
    {
      notes: {
        label: 'Notes (Optional)',
        width: '300px',
      },
    },
  ];

  const renderCell = (
    props: ActionTable.RenderCellType<
      GetDraftOrderLineType,
      { index: number; total: number }
    >,
  ) => {
    const { colKey, cellValue, rowIdx, rowValue } = props;

    switch (colKey) {
      case 'index':
        return <Text color="black">{rowIdx + 1}</Text>;
      case 'needed_by':
        return (
          <Text color="black">
            {dayjs(cellValue).format('DD MMM YYYY, hh:mm a')}
          </Text>
        );
      case 'part_id':
      case 'part_number':
      case 'qty':
      case 'notes':
      case 'release':
        return <Text color="black">{cellValue}</Text>;
      case 'unit_price':
        return (
          <Text color="black">
            <FormatNumber
              value={Number(cellValue)}
              style="currency"
              currency="USD"
            />
          </Text>
        );
      case 'total': {
        const item = rowValue;
        return (
          <Text color="black">
            <FormatNumber
              value={
                item?.unit_price && item?.qty
                  ? Number(item?.unit_price) * item?.qty
                  : 0
              }
              style="currency"
              currency="USD"
            />
          </Text>
        );
      }

      default:
        return cellValue?.value ?? cellValue ?? '';
    }
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      {/* Supplier */}
      <Flex gapX={2} fontSize="sm">
        <Text fontWeight="bold">Supplier:</Text>
        <Text>{supplierName}</Text>
      </Flex>
      <Flex flexDirection="column" bg="#ede3e0" padding={4}>
        <Flex
          bg="primary.fg"
          gapX={4}
          padding={2}
          alignItems="center"
          borderTopRadius="md"
        >
          <Image
            src="/logo-karas.png"
            alt="Karas logo"
            height="40px"
            width="100px"
          />
          <Text fontWeight="bold" color="black">
            Purchase Order
          </Text>
        </Flex>
        <Flex padding={2} gapX={10}>
          <Badge
            bg="transparent"
            border="1px solid"
            borderColor="black"
            color="black"
            fontWeight="bold"
            width="fit-content"
          >
            <Text>Draft: {draftOrder?.title}</Text>
          </Badge>
          {draftOrder?.function_number && (
            <Badge
              bg="transparent"
              border="1px solid"
              borderColor="black"
              color="black"
              fontWeight="bold"
              width="fit-content"
            >
              <Text>Function: {draftOrder.function_number}</Text>
            </Badge>
          )}
          <Badge
            bg="transparent"
            border="1px solid"
            borderColor="black"
            color="black"
            fontWeight="bold"
            width="fit-content"
          >
            <Text>By: {currentUser?.display_name}</Text>
          </Badge>
          <Badge
            bg="transparent"
            border="1px solid"
            borderColor="black"
            color="black"
            fontWeight="bold"
            width="fit-content"
          >
            <Text>Project: {selectedProject?.item?.name}</Text>
          </Badge>
          {!isNew && !!release_numbers?.length && (
            <Badge
              bg="transparent"
              border="1px solid"
              borderColor="black"
              color="black"
              fontWeight="bold"
              width="fit-content"
            >
              <Text>Release: {release_numbers?.join(', ')}</Text>
            </Badge>
          )}
          <Badge
            bg="transparent"
            border="1px solid"
            borderColor="black"
            color="black"
            fontWeight="bold"
            width="fit-content"
          >
            <Text>Date: {dayjs().format('YYYY-MM-DD')}</Text>
          </Badge>
        </Flex>
        <ActionTable.Provider>
          <Flex flexDirection="column" gapY={2}>
            <Box display="flex" flexDirection="row" gap={5}>
              <Box h="100%" my={5}>
                <ActionTable<
                  GetDraftOrderLineType,
                  { index: number; total: number }
                >
                  key={`purchase-order-table-${draftOrder?.title}-${draftOrderLines?.length}`}
                  width="76.5vw"
                  columns={columns}
                  columnToUseAsRowKey="index"
                  items={draftOrderLines ?? []}
                  isPaginated={false}
                  isPaginatedAsync={false}
                  totalItemsCount={draftOrderLines?.length ?? 0}
                  isSortable={true}
                  isFilterable={true}
                  isShowActionBar={false}
                  height={'55vh'}
                  rowHeight={60}
                  isLoading={draftOrderLines === undefined}
                  isMultipleSelection={false}
                  renderCell={renderCell}
                  isSelectionDisabled={true}
                />
              </Box>
            </Box>
          </Flex>
        </ActionTable.Provider>
        <Separator marginY={4} />
        <Flex justifyContent="space-between" fontSize="sm">
          <Flex justifyContent="flex-end" flexDirection="column">
            <Button
              size="sm"
              variant="outline"
              color="black"
              borderColor="black"
              onClick={onClickBack}
            >
              Back to Lines
            </Button>
          </Flex>
          <Flex
            flexDirection="column"
            alignItems="flex-end"
            color="black"
            gapY={2}
          >
            <Text>
              Total:{' '}
              <strong>
                <FormatNumber
                  value={calculatedPrice.total}
                  style="currency"
                  currency="USD"
                />
              </strong>
            </Text>
            <Button
              size="sm"
              marginTop={2}
              disabled={!draftOrderLines?.length}
              onClick={handleConvertToPo}
            >
              Send PO
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
export default CreatePurchaseOrder;

const draftOrderLinesSchema = z.object({
  qty: z.number(),
  unit_price: z.string(),
  needed_by: z.string(),
  part_id: z.number().optional().nullable(), // (REQUIRED -> System parts & NULL -> Manual parts), Show the part id for system parts
  part_number: z.string(), // (REQUIRED -> System parts number | manual part number), Show the part number for the part
  notes: z.string().optional(),
  release: z.number()?.optional(),
});
type GetDraftOrderLineType = z.infer<typeof draftOrderLinesSchema>;

const submitOrderToPoSchema = z.object({
  project_po_number: z.string().max(100).optional(),
  expected_delivery_date: z
    .string()
    .nonempty({ error: 'Expected delivery date is required' })
    .refine((val) => dayjs(val).isValid(), {
      error: 'Select a valid date',
    })
    .refine((val) => dayjs(val).isAfter(dayjs()), {
      error: 'Expected delivery date must be in the future',
    }),
});
type SubmitOrderToPoType = z.infer<typeof submitOrderToPoSchema>;
